import { useEffect, useState } from "react";
import { Eye, Users, Clock, Share2, Globe, Info } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";

const HOURS = Array.from({ length: 24 }, (_, h) => h);
const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const fmtHour = (h) => `${String(h).padStart(2, "0")}:00`;

export default function AdminAnalytics() {
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get("/admin/analytics", { params: { days } })
      .then((r) => setData(r.data))
      .catch((e) => toast.error(errMsg(e)))
      .finally(() => setLoading(false));
  }, [days]);

  const heatMax = data ? Math.max(1, ...Object.values(data.heat || {}).flatMap((h) => Object.values(h))) : 1;
  const hourMax = data ? Math.max(1, ...data.by_hour) : 1;
  const peakHour = data ? data.by_hour.indexOf(Math.max(...data.by_hour)) : 0;
  const topSource = data?.sources?.[0];
  const srcMax = data?.sources?.length ? Math.max(1, ...data.sources.map((s) => s.views)) : 1;
  const dates = data ? Object.keys(data.heat || {}).sort() : [];

  return (
    <div className="space-y-6" data-testid="admin-analytics">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-display text-2xl font-bold text-navy">Website visitors</h2>
          <p className="text-sm text-slate-500 mt-0.5">Live first-party traffic on btmedu.co.za — hourly heat and where visitors come from.</p>
        </div>
        <div className="flex gap-1.5" data-testid="analytics-period">
          {[[1, "Today"], [7, "7 days"], [30, "30 days"]].map(([d, label]) => (
            <button key={d} onClick={() => setDays(d)}
              className={`h-9 px-4 rounded-full text-xs font-bold transition-colors ${days === d ? "bg-navy text-white" : "bg-white border border-slate-200 text-navy hover:bg-cream"}`}
              data-testid={`period-${d}`}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {loading || !data ? (
        <div className="bg-white rounded-3xl border border-slate-200/80 p-12 text-center text-sm text-slate-400" data-testid="analytics-loading">Loading visitor data…</div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {[
              { label: "Page views", value: data.total_views, icon: Eye, testid: "stat-views" },
              { label: "Unique visitors", value: data.unique_visitors, icon: Users, testid: "stat-uniques" },
              { label: "Busiest hour", value: data.total_views ? fmtHour(peakHour) : "—", icon: Clock, testid: "stat-peak" },
              { label: "Top source", value: topSource ? `${topSource.source} (${topSource.views})` : "—", icon: Share2, testid: "stat-source" },
            ].map((c) => (
              <div key={c.label} className="bg-white rounded-2xl border border-slate-200/80 p-4" data-testid={c.testid}>
                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5"><c.icon size={12} /> {c.label}</p>
                <p className="font-display text-2xl font-bold text-navy mt-1">{c.value}</p>
              </div>
            ))}
          </div>

          {data.tracking_since && (
            <p className="text-[11px] text-slate-400 flex items-center gap-1.5" data-testid="tracking-since">
              <Info size={12} /> First-party tracking began {new Date(data.tracking_since).toLocaleString("en-ZA")} — visits before that were not recorded.
            </p>
          )}

          <div className="bg-white rounded-3xl border border-slate-200/80 p-6" data-testid="heat-grid-card">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
              <h3 className="font-display font-bold text-navy">Hourly heat <span className="text-xs font-body font-normal text-slate-400">(South African time)</span></h3>
              <div className="flex items-center gap-1.5 text-[10px] text-slate-400">
                Quiet
                {[0.15, 0.4, 0.7, 1].map((o) => <span key={o} className="w-3.5 h-3.5 rounded" style={{ backgroundColor: `rgba(232,114,42,${o})` }} />)}
                Busy
              </div>
            </div>
            {dates.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-8" data-testid="heat-empty">No visits recorded in this period yet.</p>
            ) : (
              <div className="overflow-x-auto">
                <div className="min-w-[760px] space-y-1">
                  <div className="flex gap-1 pl-16">
                    {HOURS.map((h) => (
                      <div key={h} className="flex-1 text-center text-[9px] font-bold text-slate-400">{h % 3 === 0 ? h : ""}</div>
                    ))}
                  </div>
                  {dates.map((d) => {
                    const dt = new Date(d + "T12:00:00");
                    return (
                      <div key={d} className="flex gap-1 items-center" data-testid={`heat-row-${d}`}>
                        <div className="w-16 shrink-0 text-[10px] font-bold text-slate-500">{DAY_NAMES[dt.getDay()]} {d.slice(8)}</div>
                        {HOURS.map((h) => {
                          const n = (data.heat[d] || {})[String(h)] || 0;
                          return (
                            <div key={h} title={`${d} ${fmtHour(h)} — ${n} view${n !== 1 ? "s" : ""}`}
                              className="flex-1 h-7 rounded flex items-center justify-center text-[9px] font-bold"
                              style={{ backgroundColor: n ? `rgba(232,114,42,${0.15 + 0.85 * (n / heatMax)})` : "#f1f5f9", color: n / heatMax > 0.55 ? "#fff" : "#94a3b8" }}
                              data-testid={`heat-cell-${d}-${h}`}>
                              {n || ""}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <div className="bg-white rounded-3xl border border-slate-200/80 p-6" data-testid="hourly-card">
            <h3 className="font-display font-bold text-navy mb-4">Views by hour <span className="text-xs font-body font-normal text-slate-400">(all days in period combined)</span></h3>
            <div className="flex items-end gap-1 h-32">
              {data.by_hour.map((n, h) => (
                <div key={h} className="flex-1 flex flex-col items-center gap-1">
                  <span className="text-[9px] font-bold text-slate-500">{n || ""}</span>
                  <div className="w-full rounded-t bg-gold/85" style={{ height: `${Math.max(n ? 6 : 2, (n / hourMax) * 88)}px` }} title={`${fmtHour(h)} — ${n} views`} data-testid={`hour-bar-${h}`} />
                  <span className="text-[9px] text-slate-400">{h % 3 === 0 ? h : ""}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-4">
            <div className="bg-white rounded-3xl border border-slate-200/80 p-6" data-testid="sources-card">
              <h3 className="font-display font-bold text-navy mb-4 flex items-center gap-2"><Share2 size={15} /> Where visitors come from</h3>
              {data.sources.length === 0 ? (
                <p className="text-sm text-slate-400 py-6 text-center">No visits yet. Share your TikTok/X posts with a link to btmedu.co.za and they will show up here.</p>
              ) : (
                <div className="space-y-2.5">
                  {data.sources.map((s) => (
                    <div key={s.source} className="flex items-center gap-3" data-testid={`source-${s.source.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
                      <span className="w-32 shrink-0 text-xs font-bold text-navy truncate">{s.source}</span>
                      <div className="flex-1 h-5 rounded-full bg-cream overflow-hidden">
                        <div className="h-full rounded-full bg-gold" style={{ width: `${(s.views / srcMax) * 100}%` }} />
                      </div>
                      <span className="w-16 text-right text-xs font-bold text-slate-500">{s.views} <span className="font-normal text-slate-400">({data.total_views ? Math.round((s.views / data.total_views) * 100) : 0}%)</span></span>
                    </div>
                  ))}
                </div>
              )}
              <p className="text-[11px] text-slate-400 mt-4">Tip: add <span className="font-mono font-bold text-navy">?utm_source=tiktok</span> (or x, facebook…) to the end of your link when posting — it guarantees the source is detected even when apps hide it.</p>
            </div>

            <div className="bg-white rounded-3xl border border-slate-200/80 p-6" data-testid="pages-card">
              <h3 className="font-display font-bold text-navy mb-4 flex items-center gap-2"><Globe size={15} /> Most viewed pages</h3>
              {data.top_pages.length === 0 ? (
                <p className="text-sm text-slate-400 py-6 text-center">No page views recorded yet.</p>
              ) : (
                <div className="space-y-1.5">
                  {data.top_pages.map((p) => (
                    <div key={p.path} className="flex items-center justify-between gap-3 text-xs py-1.5 border-b border-slate-50 last:border-0" data-testid={`page-row`}>
                      <span className="font-mono text-navy truncate">{p.path}</span>
                      <span className="font-bold text-slate-500 shrink-0">{p.views} view{p.views !== 1 ? "s" : ""}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
